/* Copyright (c) 2020-present CloudIO Inc */

import { getDefaultValues } from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { getManager } from '../../registry/Manager';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { propertyValueTypes } from '../../registry/constants';
import { isEmpty, isNotEmpty } from '../../utils/isEmpty';
import { ActionUpdateProp, defaultValue } from './ActionUpdateProp';
import { getPropertyDef } from 'layout/Sidebar/panels/PropertiesView/property-editors/PropertyEditorUtils';

const ActionUpdatePropManager: ActionManagerFn<'ActionUpdateProp'> = (
  platform: Platform,
): ActionManager<'ActionUpdateProp'> => {
  return {
    ...baseActionManager(platform, 'ActionUpdateProp'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Item ID',
        type: 'ItemID',
        required: true,
        helpText:
          'Choose the Item ID for which the property need to be updated.',
      },
      property: {
        label: 'Property',
        type: 'PropertyChooser',
        required: true,
        itemIdProp: 'targetItemId',
      },
      propertyValueType: {
        label: 'Value Type',
        type: 'select',
        options: propertyValueTypes,
        required: true,
        helpText: `Use Static if you want to update the property value as if it was updated in design mode. i.e. any expression used below will be evaluated at runtime during the component render cycle.

Use Expression if you want to update the property value to a specific value, any expression used below will be evaluated at the time of this action execution.`,
      },
      propertyValue: {
        label: 'Property Value',
        type: 'PropertyValue',
        itemIdProp: 'targetItemId',
        propertyProp: 'property',
        required: (item: RequiredItem<'ActionUpdateProp'>) =>
          item.props.propertyValueType === 'static',
      },
      propertyValueExpression: {
        label: 'Property Value Expression',
        type: 'expression',
        returnType: async (
          _item: RequiredItem<'ActionUpdateProp'>,
          { appUid, pageId, itemId },
        ) => {
          const item = platform.getItem(
            appUid,
            pageId,
            _item.props.targetItemId,
          );
          if (!item?.type) {
            return 'any';
          }
          const tmgr = getManager(item.type);
          const tdef = getPropertyDef(tmgr, _item.props.property);

          switch (tdef.type) {
            case 'boolean':
            case 'booleanExpression': {
              return 'boolean';
            }
            case 'number':
            case 'numberInput': {
              return 'number';
            }
            case 'string':
            case 'ColorExpression':
            case 'expressionOnly':
            case 'expression': {
              return 'string';
            }
            default:
              return 'any';
          }
        },
        required: (item: RequiredItem<'ActionUpdateProp'>) =>
          item.props.propertyValueType !== 'static',
      },
    },
    name: 'Update Property',
    getDisplayText: (item) =>
      `${item.props.property} of ${item.props.targetItemId}`,
    isPropertyVisible(
      prop,
      { targetItemId, property, propertyValueType },
    ): boolean {
      if (isEmpty(targetItemId) && prop !== 'targetItemId') {
        return false;
      }
      switch (prop) {
        case 'propertyValueType':
          return isNotEmpty(property);
        case 'propertyValue':
          return isNotEmpty(property) && propertyValueType === 'static';
        case 'propertyValueExpression':
          return isNotEmpty(property) && propertyValueType === 'expression';
        default:
          return true;
      }
    },
    onPropertyChange(
      property,
      value,
      props,
      { appUid, pageId, mgr },
    ): Partial<ActionUpdateProp> {
      if (property === 'targetItemId') {
        return {
          [property]: value,
          property: '',
          propertyValueType: props.propertyValueType || 'static',
          propertyValue: undefined,
          propertyValueExpression: { value: '', type: 'text' },
        };
      }
      if (property === 'property') {
        // replace propertyValue with default value whenever the property is changed
        // to avoid invalid value type being left out in propertyValue
        // which could cause runtime errors
        const item = platform.getItem(appUid, pageId, props.targetItemId);
        if (!item?.type) {
          return {
            [property]: value,
          };
        }
        const tmgr = getManager(item.type);
        const val = getDefaultValues<unknown>(tmgr, value as string);

        return {
          [property]: value,
          propertyValueType: props.propertyValueType || 'static',
          propertyValue: val,
          propertyValueExpression: { value: '', type: 'text' },
        };
      }
      return {
        [property]: value,
      };
    },
    icon: { iconName: 'edit' },
    description: `Use this action to programmatically update any property of a UI Component. Below are some of the examples:

- Activating a Tab in a Tab Panel
- Changing a Field Label based on the current record context
- Conditionally making a Data Store readonly
`,
  };
};

export default ActionUpdatePropManager;
